import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, Star, X } from "lucide-react";

const LOCATION_CODE_MAP: Record<string, string> = {
  "amazon-kohls": "K",
  "amazon-ups": "U",
  "amazon-fedex": "F",
  "amazon-staples": "S",
  "amazon-wholefoods": "W",
};

export interface PackageRowData {
  retailer: string;
  locationCode: string;
  size: string;
  highValue: boolean;
  returnByDate: string;
  qrCodeFile: File | null;
  packagePhotoFile: File | null;
}

export const createEmptyPackageRow = (): PackageRowData => ({
  retailer: "",
  locationCode: "",
  size: "",
  highValue: false,
  returnByDate: "",
  qrCodeFile: null,
  packagePhotoFile: null,
});

interface PackageRowInputProps {
  index: number;
  data: PackageRowData;
  onChange: (index: number, data: PackageRowData) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
  totalCount: number;
}

export const PackageRowInput = ({
  index,
  data,
  onChange,
  onRemove,
  canRemove,
  totalCount,
}: PackageRowInputProps) => {
  const update = (partial: Partial<PackageRowData>) => {
    onChange(index, { ...data, ...partial });
  };

  return (
    <div className="relative border rounded-lg p-4 space-y-4 bg-card">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">
          Package {index + 1}{totalCount > 1 ? ` of ${totalCount}` : ""}
        </h4>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Return Location */}
      <div className="space-y-2">
        <Label>Return Location</Label>
        <Select
          value={data.retailer}
          onValueChange={(value) => {
            update({ retailer: value, locationCode: LOCATION_CODE_MAP[value] || "" });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Please Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="amazon-kohls">Kohl's</SelectItem>
            <SelectItem value="amazon-ups">UPS</SelectItem>
            <SelectItem value="amazon-fedex">FedEx</SelectItem>
            <SelectItem value="amazon-staples">Staples</SelectItem>
            <SelectItem value="amazon-wholefoods">Whole Foods</SelectItem>
          </SelectContent>
        </Select>
        {data.locationCode && (
          <p className="text-xs text-muted-foreground">
            Write <span className="font-semibold text-foreground">{data.locationCode}</span> on the box
          </p>
        )}
      </div>

      {/* Size */}
      <div className="space-y-2">
        <Label>Package Size</Label>
        <Select
          value={data.size}
          onValueChange={(value) => update({ size: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Please Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Small (shoe box)</SelectItem>
            <SelectItem value="medium">Medium (standard box)</SelectItem>
            <SelectItem value="large">Large (suitcase)</SelectItem>
            <SelectItem value="oversized">Oversized (special approval required)</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 pt-1">
          <Checkbox
            id={`highValue-${index}`}
            checked={data.highValue}
            onCheckedChange={(checked) => update({ highValue: checked === true })}
          />
          <label
            htmlFor={`highValue-${index}`}
            className="text-sm font-medium leading-none cursor-pointer flex items-center gap-1.5"
          >
            High-value item
            <Star className="w-3.5 h-3.5 text-purple-500 fill-purple-500" />
          </label>
        </div>
        {data.highValue && (
          <p className="text-xs text-purple-600 dark:text-purple-400">
            This item will receive extra care and attention during pickup and delivery.
          </p>
        )}

        {data.size === "oversized" && (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm space-y-2">
            <p className="font-medium text-amber-800 dark:text-amber-200">A Quick Note From Us</p>
            <p className="text-amber-700 dark:text-amber-300">
              Returnify is still in its early stages. At this time, we're best equipped to handle packages up to the size of a standard large suitcase. If you have a larger or oversized package, feel free to reach out at{" "}
              <a href="mailto:returnify.help@gmail.com" className="underline font-medium text-amber-800 dark:text-amber-200">
                returnify.help@gmail.com
              </a>.
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
              Special approval required before scheduling
            </p>
          </div>
        )}

        {(!data.size || data.size !== "oversized") && (
          <p className="text-xs text-muted-foreground">
            No furniture-sized items (couches, TVs, chairs)
          </p>
        )}
      </div>

      {/* Return By Date */}
      <div className="space-y-2">
        <Label>Return By Date (Optional)</Label>
        <Input
          type="date"
          value={data.returnByDate}
          onChange={(e) => update({ returnByDate: e.target.value })}
          min={new Date().toISOString().split("T")[0]}
        />
        <p className="text-xs text-muted-foreground">
          If your return has a deadline, enter it here to help us prioritize
        </p>
      </div>

      {/* Package Photo */}
      <div className="space-y-2">
        <Label>Package Photo</Label>
        <label>
          <div className="border-2 border-dashed border-input rounded-lg p-4 cursor-pointer hover:border-primary transition-colors">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ImageIcon className="w-4 h-4" />
              {data.packagePhotoFile ? data.packagePhotoFile.name : "Upload package photo"}
            </div>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => update({ packagePhotoFile: e.target.files?.[0] || null })}
            className="hidden"
          />
        </label>
        <p className="text-xs text-muted-foreground">Photo showing QR code taped to package</p>
      </div>

      {/* QR Code */}
      <div className="space-y-2">
        <Label>Return QR Code</Label>
        <label className="block">
          <div className="border-2 border-dashed border-input rounded-lg p-4 cursor-pointer hover:border-primary transition-colors">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Upload className="w-4 h-4" />
              {data.qrCodeFile ? data.qrCodeFile.name : "Upload QR code image"}
            </div>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => update({ qrCodeFile: e.target.files?.[0] || null })}
            className="hidden"
          />
        </label>
      </div>

    </div>
  );
};
